import {
  Box,
  Card,
  CardHeader,
  CircularProgress,
  Switch,
  Typography,
} from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import { ReactElement, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { GuardrailsService } from 'services/GuardrailsService';
import { AppPrimaryButton } from 'shared/components/AppComponents';
import { Toast, Utils } from 'shared/helpers';
import { IAppState } from 'state/RootReducer';

interface PolicyOption {
  _id: string;
  name: string;
  description: string;
  enabled: boolean;
}

interface PolicySettingItem {
  _id: string;
  name: string;
  description: string;
  options: PolicyOption[];
  updatedAt?: string;
  updatedBy?: string;
}

interface PolicyData {
  updatedBy: string | null;
  _id: string;
  type: string;
  settings: PolicySettingItem[];
  isActive: boolean;
  organizationId: string | null;
}

interface FormValues {
  [key: string]: boolean;
}

const PolicySetting = (): ReactElement => {
  const [isLoading, setIsLoading] = useState(false);
  const [policyData, setPolicyData] = useState<PolicyData | null>(null);
  const [initialValues, setInitialValues] = useState<FormValues>({});
  const { orgACLConfig } = useSelector(
    (state: IAppState) => state.baseAppState
  );

  const actionOnSubmit = (
    formValues: FormValues,
    formikHelper: FormikHelpers<FormValues>
  ): void => {
    if (!policyData?._id) {
      Toast.showErrorMessage('Policy data not found');
      return;
    }

    setIsLoading(true);
    formikHelper.setSubmitting(true);

    const updatedPayload = policyData.settings.map((setting) => ({
      ...setting,
      options: setting.options.map((option) => ({
        ...option,
        enabled: formValues[option._id],
      })),
    }));

    GuardrailsService.updateProxySettings({
      settings: updatedPayload,
    })
      .then((res) => {
        if (res.data.msg) {
          Toast.showSuccessMessage('Proxy setting updated successfully');

          // update local policy state
          setPolicyData((prev) => {
            if (!prev) {
              return prev;
            }

            return {
              ...prev,
              settings: updatedPayload,
            };
          });

          // update initial values
          const updatedFormValues: FormValues = {};

          updatedPayload.forEach((setting) => {
            setting.options.forEach((option) => {
              updatedFormValues[option._id] = option.enabled;
            });
          });

          setInitialValues(updatedFormValues);
        }
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setIsLoading(false);
        formikHelper.setSubmitting(false);
      });
  };

  const loadAllProxy = (): void => {
    setIsLoading(true);

    GuardrailsService.getAllProxy()
      .then((res) => {
        const data: PolicyData = res.data.data[0];

        setPolicyData(data);

        const formValues: FormValues = {};

        data.settings.forEach((setting) => {
          setting.options.forEach((option) => {
            formValues[option._id] = option.enabled;
          });
        });

        setInitialValues(formValues);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    loadAllProxy();
  }, []);

  if (isLoading) {
    return (
      <Box className="empty-content">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validateOnMount
      onSubmit={(formValues, formik) => {
        actionOnSubmit(formValues, formik);
      }}
    >
      {({
        handleSubmit,
        isSubmitting,
        isValid,
        values,
        setFieldValue,
        dirty,
      }) => (
        <>
          {policyData?.settings.map((setting) => (
            <Card variant="outlined" className="mt-4" key={setting._id}>
              <CardHeader
                title={setting.name}
                subheader={setting.description}
              />

              {setting.options.map((option) => (
                <Box className="justify-space m-4" key={option._id}>
                  <Box>
                    <Typography variant="h6" className="fw-5">
                      {option.name}
                    </Typography>

                    <Typography variant="body1" className="text-tertiary">
                      {option.description}
                    </Typography>
                  </Box>

                  <Switch
                    checked={values[option._id] || false}
                    onChange={(e) => {
                      if (
                        orgACLConfig.policies?.includes(
                          'policies.guardrails.proxy.settings'
                        )
                      ) {
                        setFieldValue(option._id, e.target.checked);
                      }
                    }}
                    color="primary"
                  />
                </Box>
              ))}
            </Card>
          ))}
          {orgACLConfig.policies?.includes(
            'policies.guardrails.proxy.settings'
          ) && (
            <Box className="justify-end mt-2">
              <AppPrimaryButton
                isLoading={isSubmitting}
                onClick={handleSubmit}
                disabled={!dirty || !isValid || isSubmitting}
                loadingText="Saving"
                title="Save Changes"
                className="btn-small"
              />
            </Box>
          )}
        </>
      )}
    </Formik>
  );
};

export default PolicySetting;
